/**
 * Settings → Skills: one library of work procedures and execution skills,
 * with one search and display filters. Filters never change what agents
 * retrieve; each row opens its own store's detail by kind and ID.
 */
import {
  ChipGroup,
  PocketRow,
  PocketSection,
  TextInput,
} from "@/components/ui";
import { useT } from "@/i18n";
import { settingsSkillsMessages } from "@/i18n/messages/settings-skills";
import { useSettingsDraftState } from "./settings-draft-state";
import { SettingsLoadStatus } from "./settings-load-status";
import { useSettingsNav } from "./settings-navigation";
import {
  SKILL_KIND_KEYS,
  filterLibrary,
  rowKey,
  useSkillLibrary,
  type SkillLibraryFilter,
} from "./skill-library-state";

const FILTERS = [
  { value: "all", labelKey: "filterAll" },
  { value: "yours", labelKey: "filterYours" },
  { value: "included", labelKey: "filterIncluded" },
  { value: "added", labelKey: "filterAdded" },
] as const satisfies ReadonlyArray<{
  value: SkillLibraryFilter;
  labelKey: string;
}>;

export function SkillsPage() {
  const t = useT(settingsSkillsMessages);
  const nav = useSettingsNav();
  const [search, setSearch] = useSettingsDraftState("skills:search", "");
  const [filter, setFilter] = useSettingsDraftState<SkillLibraryFilter>(
    "skills:filter",
    "all",
  );
  const library = useSkillLibrary();
  const visible = filterLibrary(library.refs, { search, filter });
  const settled =
    !library.work.loading &&
    !library.agent.loading &&
    !library.work.error &&
    !library.agent.error;
  return (
    <div data-testid="settings-skills-section">
      <div className="px-2 pb-3">
        <TextInput
          shape="capsule"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("searchSkills")}
          aria-label={t("searchSkills")}
          data-testid="skills-search"
        />
      </div>
      <ChipGroup
        value={filter}
        onChange={setFilter}
        variant="chips"
        ariaLabel={t("showSkills")}
        options={FILTERS.map((option) => ({
          value: option.value,
          label: t(option.labelKey),
          testId: `skills-filter-${option.value}`,
        }))}
      />
      <SettingsLoadStatus
        state={library.work}
        noun={t("workProceduresNoun")}
        retryTestId="skills-retry-work"
      />
      <SettingsLoadStatus
        state={library.agent}
        noun={t("executionSkillsNoun")}
        retryTestId="skills-retry-agent"
      />
      {settled && !visible.length && (
        <p className="px-2 py-2 text-appkit-small text-muted">
          {search.trim() ? t("noSkillsMatch") : t("noSkillsInView")}
        </p>
      )}
      {visible.length > 0 && (
        <PocketSection testId="skills-library">
          {visible.map((skill) => (
            <PocketRow
              key={rowKey(skill)}
              testId={
                skill.kind === "work"
                  ? `work-skill-open-${skill.id}`
                  : `settings-skill-${skill.slug}`
              }
              title={skill.title}
              secondLine={skill.description}
              trailing={
                <span
                  className="block max-w-40 truncate"
                  title={t(SKILL_KIND_KEYS[skill.kind])}
                >
                  {t(SKILL_KIND_KEYS[skill.kind])}
                </span>
              }
              chevron
              onSelect={() =>
                nav.push({
                  page: "skills-skill",
                  args: {
                    skillId: skill.id,
                    title: skill.title,
                    kind: skill.kind,
                  },
                })
              }
            />
          ))}
        </PocketSection>
      )}
      <p className="px-2 py-2 text-appkit-small text-muted">
        {t("notAssignedAutomatically")}
      </p>
      <PocketRow
        testId="skills-browse"
        title={t("browseAllSkills")}
        chevron
        onSelect={() => nav.push({ page: "skills-browse" })}
      />
    </div>
  );
}
